import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Table, Space, Modal, Input, Dropdown } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { Form } from "antd";
import PriceCalculatorModal from "./PriceCalculatorModal";
import { axiosInstance, BASE_API_CONFIG } from "../../util/Api";
import { useParams, useLocation } from "react-router-dom";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";
import { useDispatch } from "react-redux";
import { FETCH_START, FETCH_SUCCESS } from "../../appRedux/types/commonTypes";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";

const PriceCalculatorDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { calculationId } = useParams();
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateForm] = Form.useForm();
  const [selectedCurrencies, setSelectedCurrencies] = useState(["INR"]);
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch();
  const { isMobile } = useGetBreakpoints();
  const calculateAmount = (rate, units, gst, gstType, exchangeRate = 1) => {
    const subtotal = rate * units * exchangeRate;
    let gstAmount = 0;

    if (gstType === "percentage") {
      gstAmount = (subtotal * gst) / 100;
    } else {
      gstAmount = typeof gst === "number" ? gst : 0;
    }

    return {
      subtotal,
      gstAmount,
      total: subtotal + gstAmount,
    };
  };
  const calculateTotalAmount = () => {
    return items.reduce((total, item) => {
      const amount = parseFloat(item.amount) || 0;
      return total + amount;
    }, 0);
  };

  const getPrimaryItemQuantity = () => {
    const primaryItem = items.find((item) => item.isPrimary === true);
    return primaryItem ? Number(primaryItem.noOfUnits) : 0;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(fetchStart());
        let idToFetch = calculationId;
        let isClone = false;

        if (!calculationId && location.state?.cloneId) {
          idToFetch = location.state.cloneId;
          isClone = true;
        }

        if (idToFetch) {
          const response = await axiosInstance.get(
            `${BASE_API_CONFIG.baseURL}/pricecalculation/${idToFetch}`
          );
          const data = response.data;

          setItems(
            data.items?.map((item, index) => ({
              ...item,
              amount: parseFloat(item.amount) || 0,
              gstAmount: Number(item.gstAmount) || 0,
              isPrimary: item.isPrimary || false,
              itemOrder: index,
            })) || []
          );

          // Only set template name if not cloning
          if (!isClone) {
            setTemplateName(data.templateName || "");
          }
        }
        dispatch(fetchSuccess());
      } catch (error) {
        console.error("Error fetching calculation:", error);
        dispatch(fetchSuccess());
      }
    };

    fetchData();
  }, [calculationId, dispatch, location.state]);

  const showModal = (index = null) => {
    if (index !== null && items[index]) {
      // Editing mode - keep all existing values
      setIsEditing(true);
      const item = items[index];
      form.setFieldsValue({
        ...item,
        gstRate: item?.gstRate || 0,
      });
      setEditingIndex(index);
    } else {
      // Adding new item
      setIsEditing(false);
      form.resetFields();
      setEditingIndex(null);

      // Get the last item's gstRate if available
      const lastItem = items.length > 0 ? items[items.length - 1] : null;
      const lastGstRate = lastItem?.gstRate || 0;

      // Determine initial currency
      const initialCurrency =
        selectedCurrencies.length > 1
          ? selectedCurrencies[1] // Use the non-INR currency if set
          : "INR"; // Default to INR

      // Set initial values
      form.setFieldsValue({
        gstRate: lastGstRate,
        currency: initialCurrency,
        ratePerUnit: 0,
        noOfUnits: 0,
      });
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      const newCurrency = values.currency;

      if (!selectedCurrencies.includes(newCurrency)) {
        setSelectedCurrencies((prev) => {
          if (prev.length < 2) return [...prev, newCurrency];
          const hasINR = prev.includes("INR");
          return hasINR && newCurrency !== "INR" ? ["INR", newCurrency] : prev;
        });
      }

      const isCustomRate = values.gstRate === "others";
      const gstValue = isCustomRate
        ? Number(values.customGstRate)
        : Number(values.gstRate);
      const ratePerUnit = Number(values.ratePerUnit);
      const units = Number(values.noOfUnits);
      const exchangeRate = Number(values.exchangeRate || 1);

      const subtotal = ratePerUnit * units * exchangeRate;
      const gstAmount = (subtotal * gstValue) / 100;
      const total = subtotal + gstAmount;

      const newItem = {
        ...values,
        ratePerUnit: ratePerUnit,
        noOfUnits: units,
        exchangeRate: exchangeRate,
        amount: total,
        gstType: "percentage",
        gstRate: gstValue.toString(),
        gstAmount: gstAmount,
        isPrimary: values.isPrimary || false,
        ...(isCustomRate && { customGstRate: gstValue }),
        itemOrder: editingIndex !== null ? editingIndex : items.length,
      };

      if (editingIndex !== null) {
        const updatedItems = [...items];
        updatedItems[editingIndex] = newItem;
        setItems(updatedItems);
      } else {
        setItems([...items, newItem]);
      }

      setIsModalVisible(false);
      form.resetFields();
    });
  };

  const handleDelete = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    const reorderedItems = newItems.map((item, newIndex) => ({
      ...item,
      itemOrder: newIndex,
    }));

    setItems(reorderedItems);
  };

  const handleEditSubmit = async () => {
    try {
      dispatch(fetchStart());
      const processedItems = items.map((item, index) => {
        const isFixedAmount = item.gstRate === "others";
        return {
          ...item,
          ratePerUnit: Number(item.ratePerUnit),
          noOfUnits: Number(item.noOfUnits),
          gstRate: Number(item.gstRate),
          exchangeRate: Number(item.exchangeRate || 1),
          amount: Number(item.amount),
          isPrimary: item.isPrimary || false,
          itemOrder: index,
          ...(isFixedAmount && { gstAmount: Number(item.gstAmount) }),
        };
      });

      const payload = {
        templateName: templateName,
        items: processedItems,
        totalAmount: calculateTotalAmount(),
      };

      await axiosInstance.put(
        `${BASE_API_CONFIG.baseURL}/pricecalculation/${calculationId}`,
        payload
      );
      dispatch(fetchSuccess());
      history.push("/price-calculator");
    } catch (error) {
      console.error("Error updating calculation:", error);
      dispatch(fetchSuccess());
    }
  };

  const handleSaveAll = () => {
    if (calculationId) {
      handleEditSubmit();
    } else {
      setIsTemplateModalVisible(true);
    }
  };

  const onCancel = () => {
    history.goBack();
  };

  const handleTemplateSubmit = async () => {
    try {
      const values = await templateForm.validateFields();
      setIsTemplateModalVisible(false);
      dispatch(fetchStart());
      const processedItems = items.map((item) => {
        const isFixedAmount = item.gstRate === "others";
        return {
          ...item,
          ratePerUnit: Number(item.ratePerUnit),
          noOfUnits: Number(item.noOfUnits),
          gstRate: isFixedAmount ? 0 : Number(item.gstRate),
          exchangeRate: Number(item.exchangeRate || 1),
          amount: Number(item.amount),
          isPrimary: item.isPrimary || false,
          itemOrder: Number(item.itemOrder),
          ...(isFixedAmount && { gstAmount: Number(item.gstAmount) }),
        };
      });

      const response = await axiosInstance.post(
        `${BASE_API_CONFIG.baseURL}/pricecalculation`,
        {
          templateName: values.templateName,
          items: processedItems,
          totalAmount: calculateTotalAmount(),
        }
      );

      history.push("/price-calculator");
    } catch (error) {
      console.error("Error saving calculation:", error);
    } finally {
      dispatch(fetchSuccess());
    }
  };
  const moreActionsRenderer = (_, __, index) => (
    <div className="myexim-action-icon" onClick={(e) => e.stopPropagation()}>
      <Dropdown
        menu={{
          items: [
            {
              label: "Edit",
              key: "edit",
              icon: <EditOutlined />,
              onClick: () => showModal(index),
            },
            {
              label: "Delete",
              key: "delete",
              icon: <DeleteOutlined />,
              onClick: () => handleDelete(index),
            },
          ],
        }}
        trigger={["click"]}
      >
        <Button type="text" icon={<MoreOutlined />} />
      </Dropdown>
    </div>
  );

  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    // {
    //   title: "Currency",
    //   dataIndex: "currency",
    //   key: "currency",
    // },
    {
      title: "Rate Per Unit",
      dataIndex: "ratePerUnit",
      key: "ratePerUnit",
      render: (value, record) => {
        const numValue = Number(value) || 0;
        const formattedValue = numValue.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return (
          <div>
            {record?.currency} {formattedValue} / {record?.uom}
          </div>
        );
      },
    },
    {
      title: "Quantity",
      dataIndex: "noOfUnits",
      key: "noOfUnits",
      render: (value, record) => (
        <div>
          {Number(value).toLocaleString("en-IN")} {record?.uom}
        </div>
      ),
    },
    {
      title: "Amount Without GST(INR)",
      key: "subtotal",
      align: "right",
      render: (_, record) => {
        const subtotal =
          record.ratePerUnit * record.noOfUnits * (record.exchangeRate || 1);
        return subtotal.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
    },
    {
      title: "GST",
      dataIndex: "gstRate",
      key: "gstRate",
      align: "right",
      render: (value) => {
        return `${value}%`;
      },
    },
    {
      title: "GST Amount(INR)",
      key: "gstAmount",
      align: "right",
      render: (_, record) => {
        const subtotal =
          record.ratePerUnit * record.noOfUnits * (record.exchangeRate || 1);
        let gstAmount = 0;
        if (record.gstType === "percentage") {
          gstAmount = (subtotal * Number(record.gstRate) || 0) / 100;
        } else {
          gstAmount = Number(record.gstAmount) || 0;
        }
        if (typeof gstAmount !== "number" || isNaN(gstAmount)) {
          gstAmount = 0;
        }

        return gstAmount.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
    },
    {
      title: "Amount With GST(INR)",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (value) =>
        Number(value).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      title: "Actions",
      key: "actions",
      render: moreActionsRenderer,
    },
  ];

const renderMobileView = () => {
    const foreignCurrencyItem = items.find((item) => item.currency !== "INR");
    const indianCurrencyItem = items.find((item) => item.currency === "INR");

    const indianCurrency = indianCurrencyItem?.currency || "INR";
    const foreignCurrency = foreignCurrencyItem?.currency;
    const exchangeRate = foreignCurrencyItem
      ? parseFloat(foreignCurrencyItem.exchangeRate)
      : 1;

    const noOfUnits = getPrimaryItemQuantity();
    const totalAmountINR = calculateTotalAmount();
    const totalAmountForeign = foreignCurrency
      ? totalAmountINR / exchangeRate
      : 0;

    const totalPricePerUnitINR = noOfUnits > 0 ? totalAmountINR / noOfUnits : 0;
    const totalPricePerUnitOther =
      noOfUnits > 0 ? totalAmountForeign / noOfUnits : 0;

    return (
      <>
        <Card bodyStyle={{ padding: 0 }}>
          {items.map((item, index) => {
            const subtotal =
              item.ratePerUnit * item.noOfUnits * (item.exchangeRate || 1);
            const gstAmount = (subtotal * Number(item.gstRate) || 0) / 100;

            return (
              <div
                key={index}
                className="gx-border-bottom"
                style={{
                  padding: "16px",
                  position: "relative",
                  paddingBottom: "8px",
                }}
              >
                <div style={{ display: "flex", justifyContent: "left", marginBottom: "12px" }}>
                  <div style={{ fontWeight: "bold", fontSize: "18px" }}>{item?.description}</div>
                </div>

                <div style={{ marginBottom: "4px" }}>
                  {/* Fixed layout using grid or flex-basis */}
                  <div style={{ 
                    display: "flex", 
                    flexWrap: "wrap",
                    gap: "8px 0",
                    marginBottom: "8px"
                  }}>
                    <div style={{ flexBasis: "45%", color: "#6B7280", fontSize: "14px", fontWeight: "550" }}>
                      Rate Per Unit:
                    </div>
                    <div style={{ flexBasis: "55%", textAlign: "right", fontSize: "14px" }}>
                      {item?.currency} {Number(item.ratePerUnit).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} / {item?.uom}
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: "flex", 
                    flexWrap: "wrap",
                    gap: "8px 0",
                    marginBottom: "8px"
                  }}>
                    <div style={{ flexBasis: "45%", color: "#6B7280", fontSize: "14px", fontWeight: "550" }}>
                      Quantity:
                    </div>
                    <div style={{ flexBasis: "55%", textAlign: "right", fontSize: "14px" }}>
                      {Number(item.noOfUnits).toLocaleString("en-IN")} {item.uom}
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: "flex", 
                    flexWrap: "wrap",
                    gap: "8px 0",
                    marginBottom: "8px"
                  }}>
                    <div style={{ flexBasis: "45%", color: "#6B7280", fontSize: "14px", fontWeight: "550" }}>
                      Amount Without GST({indianCurrency}):
                    </div>
                    <div style={{ flexBasis: "55%", textAlign: "right", fontSize: "14px" }}>
                      {subtotal.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: "flex", 
                    flexWrap: "wrap",
                    gap: "8px 0",
                    marginBottom: "8px"
                  }}>
                    <div style={{ flexBasis: "45%", color: "#6B7280", fontSize: "14px", fontWeight: "550" }}>
                      GST ({item.gstRate}%)({indianCurrency}):
                    </div>
                    <div style={{ flexBasis: "55%", textAlign: "right", fontSize: "14px" }}>
                      {gstAmount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: "flex", 
                    flexWrap: "wrap",
                    gap: "8px 0",
                    marginBottom: "12px",
                    paddingTop: "8px",
                    borderTop: "1px dashed #eee"
                  }}>
                    <div style={{  flexBasis: "50%", color: "#666", fontSize: "14px", fontWeight: "bold" }}>
                      Total Amount({indianCurrency}):
                    </div>
                    <div style={{ flexBasis: "50%", textAlign: "right", fontSize: "14px", fontWeight: "bold" }}>
                      {Number(item.amount).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                  
                  <div
                    style={{ position: "absolute", right: "8px", top: "12px" }}
                  >
                    {moreActionsRenderer(null, null, index)}
                  </div>
                </div>
              </div>
            );
          })}
        </Card>
        <Card bodyStyle={{ padding: "4px" }}>
          <div style={{ fontSize: "14px", fontWeight: "bold" }}>
            <div
              style={{
                margin: "12px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>Total Price / Unit ({indianCurrency}):</span>
              <span>
                {totalPricePerUnitINR.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div
              style={{
                margin: "12px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>Total Amount ({indianCurrency}):</span>
              <span>
                {totalAmountINR.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            {foreignCurrency && (
              <>
                <div
                  style={{
                    margin: "12px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>Total Price / Unit ({foreignCurrency}):</span>
                  <span>
                    {totalPricePerUnitOther.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div
                  style={{
                    margin: "12px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>Total Amount ({foreignCurrency}):</span>
                  <span>
                    {totalAmountForeign.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </>
            )}
          </div>
        </Card>
      </>
    );
  };

  return (
    <>
      <Card
        title={
          <div className="myexim-table-title">
            {calculationId ? "View Price Calculation" : "Price Calculation"}
          </div>
        }
        extra={
          <Space>
              <Button type="primary" onClick={() => showModal()}>
               { isMobile? <PlusOutlined /> : <><PlusOutlined /> Add Item </>  }
              </Button>
          </Space>
        }
      >
        {isMobile ? (
          renderMobileView()
        ) : (
          <Table
            rowClassName={(record, index) =>
              index % 2 === 0 ? "table-row-light" : "table-row-dark"
            }
            className="gx-table-responsive rfq-table myexim-table"
            columns={columns}
            dataSource={items}
            pagination={false}
            rowKey={(record, index) => index}
            locale={{
              emptyText: 'No items added yet. Click "Add Item" to get started.',
            }}
            footer={() => {
              const amountColIndex = columns.findIndex(
                (col) => col.key === "amount"
              );
              const amountCol = columns[amountColIndex];
              // const noOfUnits = Number(items?.[0]?.noOfUnits) || 0;
              const noOfUnits = getPrimaryItemQuantity();

              const foreignCurrencyItem = items.find(
                (item) => item.currency !== "INR"
              );
              const indianCurrencyItem = items.find(
                (item) => item.currency === "INR"
              );

              const indianCurrency = indianCurrencyItem?.currency || "INR";
              const foreignCurrency = foreignCurrencyItem?.currency;

              const exchangeRate = foreignCurrencyItem
                ? parseFloat(foreignCurrencyItem.exchangeRate)
                : 2;

              const totalAmountINR = calculateTotalAmount();
              const totalAmountForeign = foreignCurrency
                ? totalAmountINR / exchangeRate
                : 0;

              // total price per unit

              const totalPricePerUnitINR =
                noOfUnits > 0 ? totalAmountINR / noOfUnits : 0;
              const totalPricePerUnitOther =
                noOfUnits > 0 ? totalAmountForeign / noOfUnits : 0;

              return (
                <div
                  style={{
                    textAlign: amountCol.align || "right",
                    fontWeight: "bold",
                    padding: "8px",
                    fontSize: isMobile ? "14px" : "20px",
                    borderTop: "1px solid #f0f0f0",
                    gap: "8px",
                  }}
                >
                  <div>
                    Total Price Per Unit ({indianCurrency}) :{" "}
                    {totalPricePerUnitINR.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <div>
                    Total Amount ({indianCurrency}) :{" "}
                    {totalAmountINR.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  {foreignCurrency && (
                    <div>
                      Total Price Per Unit ({foreignCurrency}) :{" "}
                      {totalPricePerUnitOther.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  )}
                  {foreignCurrency && (
                    <div>
                      Total Amount ({foreignCurrency}) :{" "}
                      {totalAmountForeign.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  )}
                </div>
              );
            }}
          />
        )}

        <PriceCalculatorModal
          visible={isModalVisible}
          onCancel={handleCancel}
          onSave={handleSave}
          form={form}
          initialValues={{
            currency: isEditing
              ? items[editingIndex]?.currency
              : selectedCurrencies.length > 1
              ? selectedCurrencies[1]
              : "INR",
            ratePerUnit: isEditing ? items[editingIndex]?.ratePerUnit || 0 : 0,
            noOfUnits: isEditing ? items[editingIndex]?.noOfUnits || 0 : 0,
            gstRate: isEditing ? items[editingIndex]?.gstRate || 0 : 0,
          }}
          isEditing={isEditing}
          previousRowData={items.length > 0 ? items[items.length - 1] : null}
          selectedCurrencies={selectedCurrencies}
          items={items}
          setItems={setItems}
        />
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
        >
          <Button type="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSaveAll}
            disabled={items.length === 0}
          >
            Save
          </Button>
        </div>
      </Card>
      <Modal
        title="Save Price Calculation"
        open={isTemplateModalVisible}
        onOk={handleTemplateSubmit}
        onCancel={() => setIsTemplateModalVisible(false)}
      >
        <Form form={templateForm}>
          <Form.Item
            name="templateName"
            label="Template Name"
            rules={[{ required: true, message: "Please enter template name" }]}
          >
            <Input placeholder="Enter template name" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PriceCalculatorDetails;
